import { useState, useEffect } from "react";
import { Box, Grid, Paper, Snackbar, Typography, Stack, Button, Modal, TextField, MenuItem } from "@mui/material";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/DashboardLayout";
import DetailPanel from "../components/Orders/DetailPanel";
import { Helmet } from "react-helmet-async";
import DashboardHeader from "../components/Dashboard/DashboardHeader";
import KanbanColumn from "../components/Orders/KanbanColumn";
import { Order, OrderStatus } from "../components/Orders/types";
import { Search } from "@mui/icons-material";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Section from "../components/Modal/Section";
import SmallSelect from "../components/Inputs/SmallSelect";
import SmallInput from "../components/Inputs/SmallInput";
import ModalActions from "../components/Modal/Actions";
import { getGuide, saveGuide, getCurrents, setStatus } from '../api/shipments'
import { getDrivers } from '../api/drivers'

import DashboardProductTable from '../components/Kitchen/Stocks'

import moment from 'moment'


// 👇 Estados visibles en el Kanban
type KanbanStatus =
  | "created"
  | "confirmed"
  | "in_transit"
  | "ending"
  | "delivered";

// 👇 Flujo del Kanban
const NEXT_STATUS: Record<KanbanStatus, KanbanStatus | null> = {
  created: "confirmed",
  confirmed: "in_transit",
  in_transit: "ending",
  ending: "delivered",
  delivered: null,
};

export default function ProductsPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const initialForm = {
    tracking_number: "",
    guide: "",
    vehicle: "",
    destinatario: "",
    fechaTraslado: "",
    direccionLlegada: "",
    direccionPartida: "",
    estimated_delivery_date: "",
    driver_id: "",
    category_id: ""
  };

  const [filters, setFilters] = useState<{
    guide: string;
    categories: string[];
  }>({
    guide: "",
    categories: [],
  });

  const [form, setForm] = useState(initialForm);

  const handleChange = (e : any) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearchTracking = async () => {

    try {
      setLoading(true)

      const response = await getGuide(form.tracking_number)

      if (response.data.status) {
        if (response.data.data) {
          setForm(response.data.data)
        } else {
          setForm({
            ...initialForm,
            tracking_number: form.tracking_number,
          });
        }
        
      } else {
        setForm({
          ...initialForm,
          tracking_number: form.tracking_number,
        });
      }
    } catch (err) {
      console.log('err', err)
      setLoading(false)
    } finally {
      setLoading(false)
    }
    


  };

  const handleGuideFilter = (e: any) => {
    setFilters((prev) => ({
      ...prev,
      guide: e.target.value,
    }));
  };

  const handleCategoryToggle = (categoryId: string) => {
    setFilters((prev : any) => {
      const exists = prev.categories.includes(categoryId);

      return {
        ...prev,
        categories: exists
          ? prev.categories.filter((c : any) => c !== categoryId)
          : [...prev.categories, categoryId],
      };
    });
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  
  const [openCancel, setOpenCancel] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false);

  const [openProductsModal, setOpenProductsModal] = useState(false);

  const handleOpenProducts = () => setOpenProductsModal(true);
  const handleCloseProducts = () => setOpenProductsModal(false);
  const [drivers, setDrivers] = useState([]);
  const [categories, setCategories] = useState([
    { id : 'consolidado', name : 'Consolidado' },
    { id : 'express', name : 'Express' },
    { id : 'courier', name : 'Courier' },
    { id : 'exclusivo', name : 'Exclusivo' },
    { id : 'otro', name : 'Otro' },
  ]);




  const [ordersByStatus, setOrdersByStatus] = useState<{
    created: Order[];
    confirmed: Order[];
    in_transit: Order[];
    ending: Order[];
    delivered: Order[];
  }>({
    created: [],
    confirmed: [],
    in_transit: [],
    ending: [],
    delivered: []
  });

  const handleCancel = (order: Order) => {
    setOrderToCancel(order);
    setCancelReason("");
    setOpenCancel(true);
  };

  const handleAdvance = async (order: Order) => {
    const currentStatus = order.status_code as KanbanStatus;
    const nextStatus = NEXT_STATUS[currentStatus];

    if (!nextStatus) return;

    try {
      setActionLoading(true);

      await changeStatus(order.id, nextStatus);

      setOrdersByStatus((prev) => ({
        ...prev,
        [currentStatus]: prev[currentStatus].filter(
          (o) => o.id !== order.id
        ),
        [nextStatus]: [
          { ...order, status_code: nextStatus },
          ...prev[nextStatus],
        ],
      }));
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };



  const changeStatus = async (
    orderId: string,
    status: OrderStatus,
    reason?: string
    ) => {
    await setStatus({ uuid: orderId, status, reason });
  };


  const confirmCancel = async () => {
    if (!orderToCancel || !cancelReason) return;

    try {
        setActionLoading(true);

        await changeStatus(
        orderToCancel.id,
        "cancelled",
        cancelReason
        );

        // Optimistic UI
        setOrdersByStatus((prev) => ({
            ...prev,
            created: prev.created.filter(o => o.id !== orderToCancel.id),
            confirmed: prev.confirmed.filter(o => o.id !== orderToCancel.id),
            in_transit: prev.in_transit.filter(o => o.id !== orderToCancel.id),
            ending: prev.ending.filter(o => o.id !== orderToCancel.id),

        }));

        setOpenCancel(false);
        setOrderToCancel(null);
        setSnackbarOpen(true);
    } catch (e) {
        console.error(e);
    } finally {
        setActionLoading(false);
    }
  };

  const handleSave = async () => {
    
    try {
      

      if (!form.driver_id) {
        alert("Seleccione un conductor");
        return;
      }

      if (!form.estimated_delivery_date) {
        alert("Ingrese la fecha estimada de entrega");
        return;
      }

      const bodyForm = {
        guide : form.guide,
        vehicle : form.vehicle,
        receiver : form.destinatario,
        transferDate : form.fechaTraslado,
        arrivalAddress : form.direccionLlegada,
        startAddress : form.direccionPartida,
        estimatedDeliveryDate : form.estimated_delivery_date,
        driverId : form.driver_id,
        categoryId : form.category_id,
      }

        const res = await saveGuide(bodyForm);

        if (res.data.status) {
          
          handleCloseProducts()
          setForm(initialForm)
          fetchOrders()

        } else {
          console.log(res.data)
          alert(res.data.error.errorMessage)
          console.log('Ocurrio un error')
        }
     


    } catch (error) {
      console.error(error);
      alert("Error al guardar");
    }
  };


  

  const fetchOrders = async () => {
        try {
        const res = await getCurrents();
        if (res.data.status) {
            setOrdersByStatus({
                created: res.data.data.created || [],
                confirmed: res.data.data.confirmed || [],
                in_transit: res.data.data.in_transit || [],
                ending: res.data.data.ending || [],
                delivered: res.data.data.delivered || [],

                
            });

        }
        } catch (e) {
        console.error(e);
        }
    };


  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const res = await getDrivers();

        if (res.data.status) {
          setDrivers(
            res.data.data.map((driver : any) => ({
              id: driver.id,
              name: driver.full_name,
            }))
          );
        }
      } catch (e) {
        console.error(e);
      }
    };

    fetchDrivers();
  }, []);

  const cancelReasons = [
    { value: "cliente_no_responde", label: "El cliente no responde" },
    { value: "error_en_orden", label: "Error en la orden" },
    { value: "solicitud_cliente", label: "Cancelado por el cliente" },
    { value: "otro", label: "Otro motivo" },
  ];
  
  
  const handleProductUpdated = () => {
    setSnackbarOpen(true);
    setSelectedProduct(null);
  };

  const filterOrders = (orders: any[]) => {
    return orders.filter((o : any) => {
      const matchGuide = filters.guide
        ? o.tracking_code.includes(filters.guide)
        : true;

      const matchCategory =
        filters.categories.length > 0
          ? filters.categories.includes(o.category)
          : true;

      return matchGuide && matchCategory;
    });
  };


  return (
    <Layout>


      <Helmet>
        <title>Rodaris - Dashboard</title>
      </Helmet>

      <DashboardHeader
        title="Órdenes en curso"
        actionLabel="Registrar Guía"
        actionIcon={<LocalShippingIcon />}
        onAddFilter={handleOpenProducts}
      />

      
      <Box sx={{ p:4 }}>
          

        <Stack direction="row" spacing={2} mb={2}>
          <SmallInput
            placeholder="Filtrar por guía"
            value={filters.guide}
            onChange={handleGuideFilter}
          />

          <Stack direction="row" spacing={1}>
            {categories.map((cat) => {
              const active = filters.categories.includes(cat.id);

              return (
                <Button
                  key={cat.id}
                  variant={active ? "contained" : "outlined"}
                  onClick={() => handleCategoryToggle(cat.id)}
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                  }}
                >
                  {cat.name}
                </Button>
              );
            })}
          </Stack>
        </Stack>

        <Grid container spacing={2}>

          
            <KanbanColumn
                title="Creado"
                items={filterOrders(ordersByStatus.created)}
                onAdvance={handleAdvance}
                onCancel={handleCancel}
            />

            <KanbanColumn
                title="Confirmado"
                items={filterOrders(ordersByStatus.confirmed)}

                onAdvance={handleAdvance}
                onCancel={handleCancel}
            />

            <KanbanColumn
                title="En tránsito"
                items={filterOrders(ordersByStatus.in_transit)}

                onAdvance={handleAdvance}
                onCancel={handleCancel}
            />

            <KanbanColumn
                title="En ruta final"
                items={filterOrders(ordersByStatus.ending)}

                onAdvance={handleAdvance}
                onCancel={handleCancel}
            />
        </Grid>


        {/* <Grid container spacing={2} mt={3}>
          <Grid item xs={12} md={12}>
            <DashboardProductTable    />
          </Grid>
        </Grid> */}
        

            
      </Box>



      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2500}
        onClose={() => setSnackbarOpen(false)}
        message="Usuario actualizado correctamente"
      />

      
      <Modal
        open={openCancel}
        onClose={() => !actionLoading && setOpenCancel(false)}
        >
        <Box
            sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            p: 2.5,
            width: 360,
            borderRadius: 2,
            border: "1px solid #eee",
            }}
        >
            <Typography fontSize={14} fontWeight={600} mb={1}>
            Cancelar órden #{orderToCancel?.tracking_code}
            </Typography>

            <Typography fontSize={12} color="text.secondary" mb={2}>
            Selecciona un motivo para cancelar esta órden.
            </Typography>

            <TextField
            select
            fullWidth
            size="small"
            label="Motivo"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            >
            {cancelReasons.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                {r.label}
                </MenuItem>
            ))}
            </TextField>

            <Stack direction="row" justifyContent="flex-end" spacing={1} mt={3}>
            <Button
                size="small"
                onClick={() => setOpenCancel(false)}
                sx={{ fontSize: 12, color: "text.secondary" }}
            >
                Volver
            </Button>

            <Button
                size="small"
                disabled={!cancelReason || actionLoading}
                onClick={confirmCancel}
                sx={{
                fontSize: 12,
                fontWeight: 500,
                color: "#991B1B",
                bgcolor: "rgba(220,38,38,0.08)",
                border: "1px solid rgba(220,38,38,0.25)",
                "&:hover": {
                    bgcolor: "rgba(220,38,38,0.12)",
                },
                }}
            >
                Confirmar cancelación
            </Button>
            </Stack>
        </Box>
      </Modal>

      <Modal
        open={openProductsModal}
        onClose={handleCloseProducts}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            width: "90%",
            maxWidth: 1000,
            maxHeight: "90vh",
            overflowY: "auto",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            pt={3}
            pl={3}
            pr={3}
          >
            <Typography fontSize={14} fontWeight={600}>
              Registrar Guía de Remisión
            </Typography>

            
          </Stack>

          {/* Buscar guía */}

          <Box sx={{ p: 3, flexGrow: 1, overflow: "auto" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>


              <Section
                title="Consulta de guía"
                description="Número de guía"
              >
                <Stack direction="row" spacing={1}>
                  <SmallInput
                    name="tracking_number"
                    value={form.tracking_number}
                    onChange={handleChange}
                    placeholder="Ingrese número de guía"
                  />
                  <Button
                    variant="contained"
                    disabled={loading}
                    onClick={handleSearchTracking}
                    sx={{
                      backgroundColor: loading ? "#555" : "#000",
                      textTransform: "none",
                      fontSize: "12px",
                      minHeight: 38,
                      height: 38,
                      px: 3,
                      boxShadow: "none",
                      borderRadius: "10px",
                      "&:hover": {
                        backgroundColor: loading ? "#555" : "#222",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {loading ? "Procesando..." : 'Buscar'}
                  </Button>
                  
                </Stack>
              </Section>

              {/* Datos obtenidos */}

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Section title="Datos de la guía" description="Número">
                    <SmallInput
                      name="numero"
                      value={form.guide}
                      onChange={handleChange}
                      disabled
                    />
                  </Section>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Section titleEmpty={true} description="Placa">
                    <SmallInput
                      name="placa"
                      value={form.vehicle}
                      onChange={handleChange}
                      placeholder="Placa"
                    />
                  </Section>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Section titleEmpty={true} description="Fecha traslado">
                    <SmallInput
                      name="fechaTraslado"
                      type="date"
                      value={form.fechaTraslado}
                      onChange={handleChange}
                      disabled
                    />
                  </Section>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Section titleEmpty={true} description="Categoría">
                    <SmallSelect
                      name="category_id"
                      value={form.category_id}
                      onChange={handleChange}
                      options={[
                        { value: "", label: "Seleccione una categoría" },
                        ...categories.map((category : any) => ({
                          value: category.id,
                          label: category.name,
                        })),
                      ]}
                    />
                  </Section>
                </Grid>
              </Grid>

              {/* <Section title="Datos de la guía" description="Número">
                <SmallInput
                  name="numero"
                  value={form.numero}
                  onChange={handleChange}
                  disabled
                />
              </Section>

              <Section description="Placa">
                <SmallInput
                  name="placa"
                  value={form.placa}
                  onChange={handleChange}
                  placeholder="Placa"
                />
              </Section> */}

              <Section description="Destinatario">
                <SmallInput
                  name="destinatario"
                  value={form.destinatario}
                  onChange={handleChange}
                  disabled
                />
              </Section>

              {/* <Section description="Fecha traslado">
                <SmallInput
                  name="fechaTraslado"
                  type="date"
                  value={form.fechaTraslado}
                  onChange={handleChange}
                  disabled
                />
              </Section> */}

              <Section description="Dirección partida">
                <SmallInput
                  name="direccionPartida"
                  value={form.direccionPartida}
                  onChange={handleChange}
                  disabled
                />
              </Section>

              <Section description="Dirección llegada">
                <SmallInput
                  name="direccionLlegada"
                  value={form.direccionLlegada}
                  onChange={handleChange}
                  disabled
                />
              </Section>

              {/* Datos adicionales */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Section
                    title="Asignación"
                    description="Fecha entrega estimada"
                  >
                    <SmallInput
                      name="estimated_delivery_date"
                      type="date"
                      value={form.estimated_delivery_date}
                      onChange={handleChange}
                    />
                  </Section>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Section titleEmpty={true} description="Conductor asignado">
                    <SmallSelect
                      name="driver_id"
                      value={form.driver_id}
                      onChange={handleChange}
                      options={[
                        { value: "", label: "Seleccione un conductor" },
                        ...drivers.map((driver : any) => ({
                          value: driver.id,
                          label: driver.name,
                        })),
                      ]}
                    />
                  </Section>
                </Grid>
              </Grid>
              {/* <Section
                title="Asignación"
                description="Fecha entrega estimada"
              >
                <SmallInput
                  name="estimated_delivery_date"
                  type="date"
                  value={form.estimated_delivery_date}
                  onChange={handleChange}
                />
              </Section>

              <Section description="Conductor asignado">
                <SmallSelect
                  name="driver_id"
                  value={form.driver_id}
                  onChange={handleChange}
                  options={drivers.map((driver) => ({
                    value: driver.id,
                    label: driver.name,
                  }))}
                />
              </Section> */}
            </Box>
          </Box>

          <ModalActions
            onCancel={handleCloseProducts}
            onSubmit={handleSave}
            loading={saving}
            submitLabel="Asignar guía"
          />
        </Box>
      </Modal>


    </Layout>
  );
}
